import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useStores from '@stores/useStores';
import { apiAuthCaller } from '@services/apiCaller';
import { ISource } from '@stores/source';
import exportResponseError from '@utils/exportResponseError';
import { FixMe } from '@utils/types';
import moment from 'moment';
import {
  ApiResponse,
  getApiErrorMessageDetails,
  getRespData,
} from '@components/sources/sourceResponse';

export const PAGE_SIZE = 10;
const POLLING_INTERVAL = 10 * 1000;

export enum JobRunResult {
  Succeeded = 'succeeded',
  Failed = 'failed',
  Running = 'running',
}

export enum SyncStatus {
  STARTING = 'starting',
  SYNCING = 'syncing',
  AVAILABLE_TO_SYNC = 'available_to_sync',
  PAUSED = 'paused',
  STOPPING = 'stopping',
}

export interface IJobRunReport {
  run_id: string;
  started_at: string;
  finished_at: string;
  snapshot_finished_at?: string;
  trigger: 'manual' | 'scheduled';
  sync_type: 'full' | 'incremental';
  metrics: {
    total: number;
    invalid: {
      total: number;
      duplicates: number;
      nulls: number;
    };
    changed: {
      total: number;
      inserts: number;
      updates: number;
      deletes: number;
    };
    successful: {
      total: number;
      inserts: number;
      updates: number;
      deletes: number;
    };
    failed: {
      total: number;
    };
    dropped?: {
      total: number;
    };
  };
  error?: string;
  state: string; // a descriptive text for the user to see the job run updates
  status: JobRunResult;
}

export interface ISyncButtonProps {
  isSourceIncremental: boolean;
  isSetupCompleted: boolean;
  status: SyncStatus | undefined;
  syncBehaviour: string;
  startSync: (syncType: string) => void;
  stopSync: () => void;
}

interface RetlSyncsContextProps {
  reports: IJobRunReport[];
  date: { from: string; to: string };
  pageNumber: number;
  showLoader: boolean;
  syncStatus: SyncStatus | undefined;
  source: Partial<ISource>;
  totalJobRuns: number;
  highlightedRunId: string;
  onDateChange: (date: { from: string; to: string }) => void;
  onPageNumberChange: (pageNumber: number) => void;
  startSync: (syncType: string) => void;
  stopSync: () => void;
}

const defaultValue: RetlSyncsContextProps = {
  reports: [],
  date: {
    from: '',
    to: '',
  },
  pageNumber: 1,
  showLoader: true,
  syncStatus: undefined,
  source: {},
  totalJobRuns: 0,
  highlightedRunId: '',
  onDateChange: () => null,
  onPageNumberChange: () => null,
  startSync: () => null,
  stopSync: () => null,
};

export type JobRunReportsResponse = ApiResponse<{ runs: IJobRunReport[]; total_runs: number }>;

const RetlSyncsContext = createContext<RetlSyncsContextProps>(defaultValue);

export const useRetlSyncs = () => useContext(RetlSyncsContext);

let timer: ReturnType<typeof setTimeout>;

const dateFormat = {
  start: 'YYYY-MM-DD 00:00:00',
  end: 'YYYY-MM-DD 23:59:59',
};

const useDateAndRunFilter = () => {
  const [highlightedRunId, setHighlightedRunId] = useState<string>('');
  const history = useHistory();
  const location = useLocation();

  const getCustomDate = () => {
    const searchParams = new URLSearchParams(location.search);
    const timeFilterValue = searchParams.get('timeFilter')?.toLowerCase();

    const periodMap = {
      week: 6, // start date is 6 days before today
      month: 29, // start date is 29 days before today
    };
    const newDate = {
      from: moment().add(-1, 'd').format(dateFormat.start),
      to: moment().format(dateFormat.end),
    };

    if (timeFilterValue === 'week' || timeFilterValue === 'month') {
      const periodInDays = periodMap[timeFilterValue];
      newDate.from = moment()
        .add(-1 * periodInDays, 'd')
        .format(dateFormat.start);
    }

    return newDate;
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const runId = searchParams.get('runId');
    if (runId) {
      setHighlightedRunId(runId);
      searchParams.delete('runId');
    }
    searchParams.delete('timeFilter');
    history.replace({ search: searchParams.toString() });
  }, []);

  return { getCustomDate, highlightedRunId };
};

export const RetlSyncsProvider: React.FC<
  React.PropsWithChildren<{
    source: ISource;
  }>
> = ({ children, source }) => {
  const { getCustomDate, highlightedRunId } = useDateAndRunFilter();

  useEffect(() => {
    const date = getCustomDate();
    setDate(date);
    startReportsPolling(date);
    return () => {
      clearTimeout(timer);
      setTimeout(() => {
        clearTimeout(timer); // clear the polling timer after some time in case where the polling function is running ATM.
      }, POLLING_INTERVAL / 2);
    };
  }, []);

  const { messagesStore, retlConnectionListStore } = useStores();

  const [destination] = source.destinations;

  const connection = retlConnectionListStore
    .getConnectionsBySourceId(source?.id || '')
    .find((connection) => connection.destinationId === destination?.id);

  const [syncStatus, setSyncStatus] = useState<SyncStatus | undefined>(
    !source.enabled || !destination.enabled ? SyncStatus.PAUSED : undefined,
  );

  const [reports, setReports] = useState<IJobRunReport[]>([]);

  const [date, setDate] = React.useState<{ from: string; to: string }>({
    from: moment().add(-1, 'd').format(dateFormat.start),
    to: moment().format(dateFormat.end),
  });

  const [showLoader, setShowLoader] = React.useState(true);

  const [pageNumber, setPageNumber] = useState(1);

  const [totalJobRuns, setTotalJobRuns] = useState(0);

  const onPageNumberChange = async (num: number) => {
    clearTimeout(timer);
    setShowLoader(true);
    await pollSyncReports(date.from, date.to, num);
    setShowLoader(false);
    setPageNumber(num);
  };

  const onDateChange = async (date: { from: string; to: string }) => {
    clearTimeout(timer);
    setPageNumber(1);
    setDate(date);

    setShowLoader(true);
    await pollSyncReports(date.from, date.to, 1);
    setShowLoader(false);
  };

  const updateSyncStatus = (reports: IJobRunReport[], isStopSyncClicked: boolean) => {
    const syncInProgress = reports.some(
      ({ status: progress }) => progress === JobRunResult.Running,
    );

    if (syncInProgress && (isStopSyncClicked || syncStatus === SyncStatus.STOPPING)) {
      setSyncStatus(SyncStatus.STOPPING);
      return;
    }
    setSyncStatus(syncInProgress ? SyncStatus.SYNCING : SyncStatus.AVAILABLE_TO_SYNC);
  };

  const pollSyncReports = async (
    from: string,
    to: string,
    pageNumber: number,
    isStopSyncClicked = false,
  ) => {
    const url = connection
      ? `/retl-connections/${connection.id}/syncs`
      : `/cloudSources/sources/jobs/${source.id}/retlsyncruns`;
    try {
      const params = {
        destination_id: destination.id,
        from: moment(from).utc().toISOString(),
        to: moment(to).utc().toISOString(),
        offset: (pageNumber - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      };

      const { data } = await apiAuthCaller().get<JobRunReportsResponse>(url, {
        params,
      });

      const { total_runs, runs } = getRespData(data);

      setTotalJobRuns(total_runs);
      setReports(runs);
      if (syncStatus === SyncStatus.PAUSED) {
        return;
      }
      updateSyncStatus(runs, isStopSyncClicked);
    } catch (err) {
      const errDetails = getApiErrorMessageDetails(err, 'Failed to fetch syncs details');
      messagesStore.showErrorMessage(errDetails.messageWithFallback);
    } finally {
      clearTimeout(timer);
      if (syncStatus !== SyncStatus.PAUSED) {
        timer = setTimeout(
          pollSyncReports,
          POLLING_INTERVAL,
          from,
          to,
          pageNumber,
          isStopSyncClicked,
        );
      }
    }
  };

  const startReportsPolling = async (date: { from: string; to: string }) => {
    setShowLoader(true);
    await pollSyncReports(date.from, date.to, 1);
    setShowLoader(false);
  };
  const handleError = (err: FixMe) => {
    const errorResponse = exportResponseError(err);
    let errorMessage;
    if (errorResponse?.status === 404) {
      errorMessage = 'Pipeline not found';
    } else {
      errorMessage = 'Something went wrong, please try again later';
    }
    messagesStore.showErrorMessage(errorMessage);
  };

  const startSync = async (syncType: string) => {
    const url = connection
      ? `/retl-connections/${connection.id}/start-sync`
      : `/cloudSources/run/${source.id}/start`;
    const startSyncPayload = connection ? { syncType } : { sync_type: syncType };
    try {
      setSyncStatus(SyncStatus.STARTING);

      await apiAuthCaller().post(url, startSyncPayload);

      clearTimeout(timer);
      pollSyncReports(date.from, date.to, pageNumber);
    } catch (err) {
      setSyncStatus(SyncStatus.AVAILABLE_TO_SYNC);
      handleError(err);
    }
  };

  const stopSync = async () => {
    const url = connection
      ? `/retl-connections/${connection.id}/stop-sync`
      : `/cloudSources/run/${source.id}/stop`;
    try {
      await apiAuthCaller().post(url, {});
      setSyncStatus(SyncStatus.STOPPING);
      clearTimeout(timer);
      pollSyncReports(date.from, date.to, pageNumber, true);
    } catch (err) {
      setSyncStatus(SyncStatus.SYNCING);
      handleError(err);
    }
  };

  const value = useMemo(
    () => ({
      reports,
      date,
      pageNumber,
      showLoader,
      syncStatus,
      source,
      totalJobRuns,
      onDateChange,
      startReportsPolling,
      startSync,
      stopSync,
      onPageNumberChange,
      highlightedRunId,
    }),
    [date, totalJobRuns, showLoader, reports, pageNumber, syncStatus],
  );

  return <RetlSyncsContext.Provider value={value}>{children}</RetlSyncsContext.Provider>;
};
